import { Fragment, useState } from 'react';
import { SearchOutlined, MessageOutlined } from '@ant-design/icons';
import { Empty as AntdEmpty, Button, Input, Pagination, Spin } from 'antd';
import debounce from 'lodash/debounce';
import styled from 'styled-components';
import { useNotificationContext } from '@/features/notifications';
import { useGetOrganizationsPublishedProcs } from '@/shared/api';
import { usePaging } from '@/shared/hooks';
import { useRouterContext } from '@/shared/router';
import { ChatMode } from '@/shared/types';
import { Backdrop } from '@/shared/ui';
import { PageTitleBanner } from '@/shared/ui/page-title-banner';
import { captureError } from '@/shared/utils';
import { OrganizationChat } from '@/widgets/chat/OrganizationChat';
import { PageLayout } from '@/widgets/page-layout';
import { ProcCard } from '@/widgets/proc-card';
import EmptyIcon from '@assets/empty.svg';
import { HeaderLogo } from './HeaderLogo';

export const ReaderDashboard = () => {
  const { showNotification } = useNotificationContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [chatVisible, setChatVisible] = useState(false);

  const { activeOrganization } = useRouterContext();
  const organizationId = activeOrganization?.id || '';

  const { page, pageSize, pageSizeOptions, onPagingChange } = usePaging({
    resetDeps: [searchTerm],
    pageSizeOptions: [50, 75],
  });
  const { data: procsData, isFetching } = useGetOrganizationsPublishedProcs(
    {
      pathParams: { id: organizationId },
      queryParams: { searchTerm, page, take: pageSize },
    },
    {
      keepPreviousData: true,
      onError: (error) => {
        captureError(error, {
          module: 'reader_dashboard',
          action: 'get_published_procs',
          metadata: { organizationId },
        });
        showNotification({
          type: 'error',
          message: 'Failed to fetch published procs',
          description: 'Please try again later',
        });
      },
    },
  );

  return (
    <PageLayout
      headerContent={<HeaderLogo />}
      contentStyle={{ paddingBottom: 40 }}
    >
      <PageTitleBanner
        title={activeOrganization?.name}
        imageUrl={activeOrganization?.uploadedImageLink}
        bannerColor={activeOrganization?.theme?.bannerColor}
      />
      <PageContent>
        <Input
          allowClear
          placeholder="Search procs..."
          suffix={<SearchOutlined />}
          onChange={debounce(({ target }) => setSearchTerm(target.value), 500)}
          style={{ marginBottom: 25 }}
        />
        {!procsData?.data?.length && !isFetching && (
          <Empty
            image={EmptyIcon}
            description="There are no procs to show"
            style={{
              width: '100%',
              height: '60vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          />
        )}
        {isFetching && (
          <Backdrop>
            <Spin />
          </Backdrop>
        )}
        <Grid>
          {procsData?.data?.map((proc, procIndex) => (
            <Fragment key={procIndex}>
              <ProcCard key={proc.id} proc={proc} type="discover" />
            </Fragment>
          ))}
        </Grid>

        <Pagination
          hideOnSinglePage
          showSizeChanger
          disabled={isFetching}
          total={procsData?.meta?.itemCount}
          current={page}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          onChange={onPagingChange}
        />

        <ChatButton
          type="primary"
          shape="circle"
          icon={<MessageOutlined />}
          onClick={() => setChatVisible(!chatVisible)}
        />

        {chatVisible && (
          <ChatWrapper>
            <OrganizationChat
              orgId={organizationId}
              chatMode={ChatMode.User}
              isOpenByDefault={true}
              isCollapsible={false}
            />
          </ChatWrapper>
        )}
      </PageContent>
    </PageLayout>
  );
};

const ChatButton = styled(Button)`
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 56px !important;
  height: 56px;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);

  svg {
    font-size: 24px;
  }
`;

const ChatWrapper = styled.div`
  position: fixed;
  right: 24px;
  bottom: 96px;
  width: 450px;
  max-width: 600px;
  z-index: 998;
  background: white;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  overflow: hidden;

  @media (max-width: 768px) {
    width: calc(100% - 48px);
    max-width: 600px;
  }
`;

const Grid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  grid-gap: 20px;
  align-items: stretch;
`;

const Empty = styled(AntdEmpty)`
  margin-inline: 0;
`;

const PageContent = styled.div`
  margin: 0 25px;
`;
